import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, BarChart3, Bot, Building2, CalendarClock,
  CheckCircle2, RefreshCw, Search, TrendingUp, Wallet,
} from "lucide-react";
import { apiPost, apiGet } from "../../utils/api";
import { getPlanDisplayName, getPlanPublicId } from "../../utils/pricingDisplay.js";
import { toast } from "../../utils/toast";
import "./admin-shell.css";

/* ═══════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════ */
const PAYMENT_SUCCESS  = new Set(["succeeded", "paid", "captured"]);
const ACTIVE_SUB       = new Set(["active", "trial"]);
const EXPIRY_WARN_DAYS = 14;

/* ═══════════════════════════════════════════════════
   Pure helpers
   ═══════════════════════════════════════════════════ */
const norm  = v => String(v || "").trim();
const normL = v => norm(v).toLowerCase();

function parseTs(v) {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function fmtDate(v, { time = false } = {}) {
  const t = parseTs(v);
  if (!t) return "-";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      ...(time ? { timeStyle: "short" } : {}),
    }).format(new Date(t));
  } catch { return new Date(t).toLocaleString(); }
}

function fmtCurrency(amount, currency = "INR") {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: String(currency || "INR").toUpperCase(),
      maximumFractionDigits: 2,
    }).format(n / 100);
  } catch { return `${(n / 100).toFixed(2)} ${currency}`; }
}

function fmtSeats(v) {
  if (v === null) return "Unlimited";
  const n = Number(v);
  return (!Number.isFinite(n) || n <= 0) ? "5" : `${n}`;
}

function getPlanLabel(sub) {
  if (!sub) return "No Plan";
  return getPlanDisplayName(sub.planId, sub.planName) || "No Plan";
}

function cmpRecency(a, b, fields = ["updatedAt", "createdAt"]) {
  for (const f of fields) {
    const d = parseTs(b?.[f]) - parseTs(a?.[f]);
    if (d !== 0) return d;
  }
  return 0;
}

function groupByTenantId(rows = []) {
  return rows.reduce((acc, r) => {
    const id = norm(r?.tenantId);
    if (!id) return acc;
    (acc[id] = acc[id] || []).push(r);
    return acc;
  }, {});
}

function resolveCurrentSub(rows = []) {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => {
    const ap = ACTIVE_SUB.has(normL(a?.status)) ? 2 : normL(a?.status) === "past_due" ? 1 : 0;
    const bp = ACTIVE_SUB.has(normL(b?.status)) ? 2 : normL(b?.status) === "past_due" ? 1 : 0;
    if (ap !== bp) return bp - ap;
    const ed = parseTs(b?.expiresAt) - parseTs(a?.expiresAt);
    if (ed !== 0) return ed;
    return cmpRecency(a, b, ["updatedAt", "createdAt", "startsAt"]);
  })[0] || null;
}

function resolveLifecycle(sub) {
  if (!sub) return "no_plan";
  const st = normL(sub.status);
  const exp = parseTs(sub.expiresAt);
  if (exp && exp < Date.now()) return "expired";
  return st || "inactive";
}

function resolveExpiry(sub) {
  const exp = parseTs(sub?.expiresAt);
  if (!exp) return { label: "No expiry", tone: "neutral", days: null };
  const days = Math.ceil((exp - Date.now()) / 86400000);
  if (days < 0)                  return { label: `Expired ${Math.abs(days)}d ago`, tone: "danger",  days };
  if (days <= EXPIRY_WARN_DAYS)  return { label: `Expires in ${days}d`,            tone: "warning", days };
  return                                 { label: `${days}d remaining`,             tone: "healthy", days };
}

function latestOf(rows = [], fields) {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => cmpRecency(a, b, fields))[0];
}
function latestPaid(rows = []) {
  return latestOf(rows.filter(r => PAYMENT_SUCCESS.has(normL(r?.status))), ["paidAt", "updatedAt", "createdAt"]);
}

function summarizeIssues({ lifecycle, failedPays = [], expiry }) {
  const out = [];
  if (failedPays.length > 0) out.push(`${failedPays.length} failed payment${failedPays.length > 1 ? "s" : ""}`);
  if (lifecycle === "expired")    out.push("plan expired");
  if (lifecycle === "past_due")   out.push("payment overdue");
  if (expiry?.tone === "warning") out.push("expiry soon");
  return out;
}

function moneyParts(src = {}) {
  const sub   = Number(src?.subtotalAmount ?? src?.subtotal ?? src?.amount ?? 0);
  const tax   = Number(src?.taxAmount || 0);
  const total = Number(src?.totalAmount ?? src?.total ?? (sub + tax));
  return { sub, tax, total, currency: String(src?.currency || "INR").toUpperCase() };
}

/* ═══════════════════════════════════════════════════
   Status badge — maps to admin-status classes
   ═══════════════════════════════════════════════════ */
function statusClass(raw) {
  const s = normL(raw);
  if (["active", "trial", "succeeded", "paid", "captured"].includes(s)) return "is-active";
  if (["past_due", "pending", "no_payment", "processing"].includes(s))  return "is-pending";
  if (["expired", "canceled", "cancelled", "no_plan", "failed", "inactive"].includes(s)) return "is-disabled";
  return "is-pending";
}

function StatusBadge({ value }) {
  const raw = normL(value) || "unknown";
  return (
    <span className={`admin-status ${statusClass(raw)}`}>
      {raw.replace(/_/g, " ")}
    </span>
  );
}

function ExpiryBadge({ meta }) {
  if (!meta) return null;
  const cls = meta.tone === "healthy" ? "is-active" : meta.tone === "warning" ? "is-pending" : meta.tone === "danger" ? "is-disabled" : "";
  return <span className={`admin-status ${cls}`}>{meta.label}</span>;
}

function MoneyLine({ src, compact }) {
  const m = moneyParts(src);
  if (compact) {
    return (
      <div style={{ fontSize: "0.78rem", color: "var(--as-text-2)", lineHeight: 1.5, marginTop: 2 }}>
        {fmtCurrency(m.total, m.currency)}
      </div>
    );
  }
  return (
    <div style={{ fontSize: "0.78rem", color: "var(--as-text-2)", lineHeight: 1.6 }}>
      <div>Base: {fmtCurrency(m.sub, m.currency)}</div>
      <div>GST:  {fmtCurrency(m.tax, m.currency)}</div>
      <div style={{ fontWeight: 600, color: "var(--as-text)" }}>Total: {fmtCurrency(m.total, m.currency)}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════ */
export default function AdminBillingOverview() {
  const navigate = useNavigate();

  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [tenants,       setTenants]       = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [invoices,      setInvoices]      = useState([]);
  const [failedPays,    setFailedPays]    = useState([]);
  const [emailLogs,     setEmailLogs]     = useState([]);

  const [view,          setView]          = useState("overview");
  const [searchTerm,    setSearchTerm]    = useState("");
  const [statusFilter,  setStatusFilter]  = useState("all");
  const [issuesOnly,    setIssuesOnly]    = useState(false);
  const [resendingId,   setResendingId]   = useState("");

  /* ── Load ── */
  const load = async ({ silent = false } = {}) => {
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const [tR, sR, pR, iR, fR, eR] = await Promise.all([
        apiGet("/api/admin/tenants"),
        apiGet("/api/admin/billing/subscriptions?limit=500"),
        apiGet("/api/admin/billing/payments?limit=500"),
        apiGet("/api/admin/billing/invoices?limit=500"),
        apiGet("/api/admin/billing/failed-payments?limit=500"),
        apiGet("/api/admin/billing/email-logs?limit=500"),
      ]);
      const [tp, sp, pp, ip, fp, ep] = await Promise.all([
        tR.json().catch(() => []),
        sR.json().catch(() => null),
        pR.json().catch(() => null),
        iR.json().catch(() => null),
        fR.json().catch(() => null),
        eR.json().catch(() => null),
      ]);
      if (!tR.ok) throw new Error(tp?.message  || "Failed to fetch tenants");
      if (!sR.ok) throw new Error(sp?.message  || "Failed to fetch subscriptions");
      if (!pR.ok) throw new Error(pp?.message  || "Failed to fetch payments");
      if (!iR.ok) throw new Error(ip?.message  || "Failed to fetch invoices");
      if (!fR.ok) throw new Error(fp?.message  || "Failed to fetch failed payments");
      if (!eR.ok) throw new Error(ep?.message  || "Failed to fetch email logs");
      setTenants(Array.isArray(tp) ? tp : []);
      setSubscriptions(Array.isArray(sp?.subscriptions) ? sp.subscriptions : []);
      setPayments(Array.isArray(pp?.payments) ? pp.payments : []);
      setInvoices(Array.isArray(ip?.invoices) ? ip.invoices : []);
      setFailedPays(Array.isArray(fp?.payments) ? fp.payments : []);
      setEmailLogs(Array.isArray(ep?.logs) ? ep.logs : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load billing overview");
    } finally {
      silent ? setRefreshing(false) : setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  /* ── Derived ── */
  const subsByTenant   = useMemo(() => groupByTenantId(subscriptions), [subscriptions]);
  const paysByTenant   = useMemo(() => groupByTenantId(payments),      [payments]);
  const invsByTenant   = useMemo(() => groupByTenantId(invoices),      [invoices]);
  const failsByTenant  = useMemo(() => groupByTenantId(failedPays),    [failedPays]);
  const emailsByTenant = useMemo(() => groupByTenantId(emailLogs),     [emailLogs]);

  const rows = useMemo(() => {
    const built = tenants.map(t => {
      const tid   = norm(t?.id);
      const subs  = subsByTenant[tid]   || [];
      const pays  = (paysByTenant[tid]  || []).sort((a, b) => cmpRecency(a, b, ["paidAt", "createdAt"]));
      const invs  = (invsByTenant[tid]  || []).sort((a, b) => cmpRecency(a, b, ["paidAt", "createdAt"]));
      const fails = (failsByTenant[tid] || []).sort((a, b) => cmpRecency(a, b, ["paidAt", "createdAt"]));
      const emails= (emailsByTenant[tid]|| []).sort((a, b) => cmpRecency(a, b, ["sentAt", "createdAt"]));

      const curSub  = resolveCurrentSub(subs);
      const latSub  = latestOf(subs, ["updatedAt", "createdAt", "startsAt"]);
      const effSub  = curSub || latSub;
      const lifecycle = resolveLifecycle(effSub);
      const expiry    = resolveExpiry(effSub);
      const issues    = summarizeIssues({ lifecycle, failedPays: fails, expiry });

      return {
        tenantId: tid, tenantName: norm(t?.name) || "Unnamed Tenant",
        ownerEmail: normL(t?.ownerEmail) || "-",
        billingEmail: normL(effSub?.billingEmail) || normL(t?.ownerEmail) || "-",
        enrollmentKey: norm(t?.enrollmentKey),
        deviceCount: Number(t?.deviceCount || 0),
        licensedSeats: t?.maxSeats === null ? null : (Number.isFinite(Number(t?.maxSeats)) ? Number(t.maxSeats) : 5),
        isTenantActive: !!t?.isActive,
        curSub, effSub, lifecycle, expiry, issues,
        latestPay: latestOf(pays, ["paidAt", "createdAt"]),
        lastPaidPay: latestPaid(pays),
        latestInv: latestOf(invs, ["paidAt", "createdAt"]),
        failedPayCount: fails.length,
        subHistory: subs, payHistory: pays, invHistory: invs, failHistory: fails, emailHistory: emails,
      };
    });
    return built.sort((a, b) => {
      if (b.failedPayCount !== a.failedPayCount) return b.failedPayCount - a.failedPayCount;
      const ed = parseTs(a.effSub?.expiresAt) - parseTs(b.effSub?.expiresAt);
      if (ed !== 0) return ed;
      return a.tenantName.localeCompare(b.tenantName);
    });
  }, [tenants, subsByTenant, paysByTenant, invsByTenant, failsByTenant, emailsByTenant]);

  const filtered = useMemo(() => {
    const q = normL(searchTerm);
    return rows.filter(r => {
      const sm = !q || [r.tenantName, r.ownerEmail, r.billingEmail,
        getPlanLabel(r.effSub), getPlanPublicId(r.effSub?.planId), norm(r.effSub?.planId)]
        .some(v => normL(v).includes(q));
      const fm = statusFilter === "all"
        || (statusFilter === "canceled" ? ["canceled","cancelled"].includes(r.lifecycle) : r.lifecycle === statusFilter);
      const im = !issuesOnly || r.issues.length > 0;
      return sm && fm && im;
    });
  }, [rows, searchTerm, statusFilter, issuesOnly]);

  const summary = useMemo(() => ({
    total: rows.length,
    subscribed: rows.filter(r => r.effSub).length,
    active: rows.filter(r => ACTIVE_SUB.has(r.lifecycle)).length,
    trial: rows.filter(r => r.lifecycle === "trial").length,
    expiringSoon: rows.filter(r => r.expiry?.tone === "warning").length,
    failing: rows.filter(r => r.failedPayCount > 0).length,
  }), [rows]);

  const mkt = useMemo(() => {
    const year = new Date().getFullYear();
    const paidThisYear = payments.filter(p => {
      if (!PAYMENT_SUCCESS.has(normL(p?.status))) return false;
      const d = new Date(p?.paidAt || p?.createdAt);
      return !Number.isNaN(d.getTime()) && d.getFullYear() === year;
    });
    const revenueThisYear = paidThisYear.reduce((s, p) => s + Number(p?.totalAmount || p?.amount || 0), 0);
    const revenueCurrency = paidThisYear[0]?.currency || "INR";

    const totalAgents   = rows.reduce((s, r) => s + r.deviceCount, 0);
    const totalLicensed = rows.reduce((s, r) => s + (r.licensedSeats === null ? 0 : (r.licensedSeats || 0)), 0);

    /* plan distribution */
    const dist = {};
    rows.forEach(r => {
      const label = getPlanLabel(r.effSub);
      dist[label] = (dist[label] || 0) + 1;
    });
    const planDist = Object.entries(dist)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    return { revenueThisYear, revenueCurrency, paidThisYearCount: paidThisYear.length, totalAgents, totalLicensed, year, planDist };
  }, [rows, payments]);

  /* ── Actions ── */
  const resendInvoice = async (inv) => {
    const id = norm(inv?.id);
    if (!id) return;
    if (!window.confirm(`Resend invoice ${inv.invoiceNumber || id}?`)) return;
    setResendingId(id);
    try {
      const res = await apiPost(`/api/admin/billing/invoices/${encodeURIComponent(id)}/resend`);
      const d = await res.json().catch(() => null);
      if (!res.ok) { toast.error(d?.message || "Failed to resend"); return; }
      toast.success(d?.message || "Invoice resend queued");
      await load({ silent: true });
    } catch { toast.error("Failed to resend invoice"); }
    finally { setResendingId(""); }
  };

  /* ── Early exit ── */
  if (loading) return (
    <div className="admin-workspace">
      <div className="admin-empty-state">Loading billing overview…</div>
    </div>
  );

  /* ── Render ── */
  return (
    <div className="admin-workspace">

      {/* Header */}
      <div className="admin-shell-header" style={{ marginBottom: 0 }}>
        <div className="admin-shell-copy">
          <span className="admin-shell-eyebrow">Revenue</span>
          <h1 className="admin-shell-title">{view === "overview" ? "Business Summary" : "Billing Accounts"}</h1>
          <p className="admin-shell-subtitle">
            {view === "overview"
              ? `${summary.total} clients · ${mkt.totalAgents.toLocaleString()} agents · ${fmtCurrency(mkt.revenueThisYear, mkt.revenueCurrency)} this year`
              : `${filtered.length} of ${rows.length} accounts`}
          </p>
        </div>
        <div className="admin-shell-actions">
          <button className="gc-icon-btn" onClick={() => load({ silent: true })} disabled={refreshing} data-tip={refreshing ? "Refreshing…" : "Refresh"}>
            <RefreshCw size={14} className={refreshing ? "ab-spin" : ""} />
          </button>
        </div>
      </div>

      {/* View tab bar */}
      <div className="admin-tabbar" style={{ borderBottom: "1px solid var(--as-border)", padding: "0 4px", marginBottom: 0 }}>
        <button className={`admin-tab${view === "overview" ? " is-active" : ""}`} onClick={() => setView("overview")}>
          Summary
        </button>
        <button className={`admin-tab${view === "requests" ? " is-active" : ""}`} onClick={() => setView("requests")}>
          Accounts
          <span className="admin-tab-badge">{rows.length}</span>
        </button>
      </div>

      {/* ══════════ SUMMARY DASHBOARD ══════════ */}
      {view === "overview" && (
        <div className="abo-dash">

          {/* Primary 3 stats */}
          <div className="abo-dash-3">
            <div className="abo-dash-stat">
              <div className="abo-dash-stat-icon"><Building2 size={18} /></div>
              <div className="abo-dash-stat-body">
                <span className="abo-dash-stat-label">Total Clients</span>
                <span className="abo-dash-stat-num">{summary.total}</span>
                <span className="abo-dash-stat-meta">{summary.subscribed} on active plans</span>
              </div>
            </div>
            <div className="abo-dash-stat">
              <div className="abo-dash-stat-icon abo-dash-stat-icon--success"><Bot size={18} /></div>
              <div className="abo-dash-stat-body">
                <span className="abo-dash-stat-label">Agents Deployed</span>
                <span className="abo-dash-stat-num">{mkt.totalAgents.toLocaleString()}</span>
                <span className="abo-dash-stat-meta">{mkt.totalLicensed.toLocaleString()} licensed seats</span>
              </div>
            </div>
            <div className="abo-dash-stat">
              <div className="abo-dash-stat-icon abo-dash-stat-icon--warning"><TrendingUp size={18} /></div>
              <div className="abo-dash-stat-body">
                <span className="abo-dash-stat-label">Revenue {mkt.year}</span>
                <span className="abo-dash-stat-num abo-dash-stat-num--sm">{fmtCurrency(mkt.revenueThisYear, mkt.revenueCurrency)}</span>
                <span className="abo-dash-stat-meta">{mkt.paidThisYearCount} transactions</span>
              </div>
            </div>
          </div>

          {/* Secondary 4 stats */}
          <div className="abo-dash-4">
            {[
              { icon: <CheckCircle2 size={14} />, num: summary.active,       label: "Active",       tone: "success" },
              { icon: <Wallet       size={14} />, num: summary.trial,        label: "Trial",        tone: "primary" },
              { icon: <CalendarClock size={14}/>, num: summary.expiringSoon, label: "Expiring Soon",tone: "warning" },
              { icon: <AlertTriangle size={14}/>, num: summary.failing,      label: "Issues",       tone: "danger"  },
            ].map(({ icon, num, label, tone }) => (
              <div key={label} className={`abo-dash-mini abo-dash-mini--${tone}`}>
                <span className="abo-dash-mini-icon">{icon}</span>
                <span className="abo-dash-mini-num">{num}</span>
                <span className="abo-dash-mini-label">{label}</span>
              </div>
            ))}
          </div>

          {/* Plan distribution */}
          {mkt.planDist.length > 0 && (
            <div className="abo-dash-dist">
              <div className="abo-dash-dist-hd">
                <BarChart3 size={14} />
                <span>Plan Distribution</span>
              </div>
              {mkt.planDist.map(([label, count]) => {
                const pct = summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;
                return (
                  <div key={label} className="abo-dash-dist-row">
                    <span className="abo-dash-dist-name">{label}</span>
                    <div className="abo-dash-dist-track">
                      <div className="abo-dash-dist-fill" style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                    <span className="abo-dash-dist-pct">{count} <small>({pct}%)</small></span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════ ACCOUNTS TABLE ══════════ */}
      {view === "requests" && (<>

      {/* Toolbar */}
      <div className="admin-toolbar" style={{ marginTop: 16 }}>
        <label className="admin-search" style={{ flex: "1 1 380px" }}>
          <Search size={14} />
          <input
            type="search"
            placeholder="Search tenant, owner, billing email, or plan…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </label>
        <select className="admin-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="past_due">Past due</option>
          <option value="expired">Expired</option>
          <option value="canceled">Cancelled</option>
          <option value="no_plan">No plan</option>
        </select>
        <label className="ab-checkbox-label">
          <input type="checkbox" checked={issuesOnly} onChange={e => setIssuesOnly(e.target.checked)} />
          Issues only
        </label>
        <span className="admin-panel-kicker">{filtered.length} of {rows.length}</span>
      </div>

      {/* Main table */}
      <div className="admin-table-wrap">
        <table className="admin-table ab-main-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Current Plan</th>
              <th>Subscription</th>
              <th>Billing Contact</th>
              <th>Seats / Devices</th>
              <th>Latest Payment</th>
              <th>Expiry</th>
              <th>Issues</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: "28px", textAlign: "center", color: "var(--as-text-2)" }}>
                No tenants match the current filters.
              </td></tr>
            ) : filtered.map(r => {
              const plan = r.effSub;
              const lp   = r.latestPay;
              return (
                <tr
                  key={r.tenantId}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/admin/billing-overview/${r.tenantId}`, { state: { row: r } })}
                >
                  <td>
                    <div className="admin-cell-title">{r.tenantName}</div>
                    <div className="admin-cell-subtitle">{r.ownerEmail}</div>
                    <div className="admin-cell-subtitle">
                      <span className={`admin-status ${r.isTenantActive ? "is-active" : "is-disabled"}`}
                        style={{ height: 18, fontSize: "0.67rem", padding: "0 6px", gap: 4 }}>
                        {r.isTenantActive ? "Active" : "Disabled"}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-cell-title">{getPlanLabel(plan)}</div>
                    {plan ? <MoneyLine src={plan} compact /> : <div className="admin-cell-subtitle">—</div>}
                    <div className="admin-cell-subtitle">{norm(plan?.billingCycle) || "—"}</div>
                  </td>
                  <td>
                    <StatusBadge value={r.lifecycle} />
                    <div className="admin-cell-subtitle">Started: {fmtDate(plan?.startsAt)}</div>
                  </td>
                  <td>
                    <div className="admin-cell-title">{r.billingEmail}</div>
                    <div className="admin-cell-subtitle">Inv: {r.latestInv?.invoiceNumber || "—"}</div>
                  </td>
                  <td>
                    <div className="admin-cell-title">{fmtSeats(r.licensedSeats)} seats</div>
                    <div className="admin-cell-subtitle">{r.deviceCount} devices</div>
                    <div className="admin-cell-subtitle">Used: {Number(plan?.usedSeatCount || 0)}</div>
                  </td>
                  <td>
                    <StatusBadge value={lp?.status || "no_payment"} />
                    {lp ? <MoneyLine src={lp} compact /> : <div className="admin-cell-subtitle">—</div>}
                    <div className="admin-cell-subtitle">{fmtDate(lp?.paidAt || lp?.createdAt)}</div>
                  </td>
                  <td>
                    <ExpiryBadge meta={r.expiry} />
                    <div className="admin-cell-subtitle">Expires: {fmtDate(plan?.expiresAt)}</div>
                    <div className="admin-cell-subtitle">Next: {fmtDate(plan?.nextBillingAt)}</div>
                  </td>
                  <td>
                    {r.issues.length > 0
                      ? r.issues.map(iss => (
                        <span key={iss} className="admin-status is-disabled" style={{ display: "block", marginBottom: 3, width: "fit-content", height: 20, fontSize: "0.68rem", padding: "0 7px" }}>
                          {iss}
                        </span>
                      ))
                      : <span style={{ fontSize: "0.8rem", color: "var(--as-success)" }}>✓ No issues</span>
                    }
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="admin-actions">
                      <button className="admin-inline-action"
                        disabled={!r.latestInv?.id || resendingId === r.latestInv?.id}
                        onClick={() => resendInvoice(r.latestInv)}>
                        {resendingId === r.latestInv?.id ? "Sending…" : "Resend Inv."}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      </>)}

      <style>{`
        @keyframes ab-spin { to { transform: rotate(360deg); } }
        .ab-spin { animation: ab-spin .8s linear infinite; }
        .ab-main-table { min-width: 1100px; }
        .ab-checkbox-label {
          display: flex; align-items: center; gap: 6px;
          font-size: .875rem; color: var(--as-text-2); cursor: pointer; white-space: nowrap;
        }

        /* ── Summary Dashboard ── */
        .abo-dash { display: flex; flex-direction: column; gap: 8px; padding-top: 8px; }

        /* Primary 3 stats */
        .abo-dash-3 {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
        }
        @media (max-width: 680px) { .abo-dash-3 { grid-template-columns: 1fr; } }
        .abo-dash-stat {
          display: flex; align-items: flex-start; gap: 14px;
          padding: 18px 16px;
          background: var(--as-surface); border: 1px solid var(--as-border);
          border-radius: var(--as-r); box-shadow: var(--as-shadow);
        }
        .abo-dash-stat-icon {
          width: 36px; height: 36px; border-radius: var(--as-r);
          display: flex; align-items: center; justify-content: center;
          background: var(--as-primary-bg); color: var(--as-primary); flex-shrink: 0;
        }
        .abo-dash-stat-icon--success { background: var(--as-success-bg); color: var(--as-success); }
        .abo-dash-stat-icon--warning { background: var(--as-warning-bg); color: var(--as-warning); }
        .abo-dash-stat-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .abo-dash-stat-label {
          font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: .05em; color: var(--as-text-2);
        }
        .abo-dash-stat-num {
          font-size: 1.75rem; font-weight: 700; line-height: 1.1;
          color: var(--as-text); letter-spacing: -.01em;
        }
        .abo-dash-stat-num--sm { font-size: 1.25rem; }
        .abo-dash-stat-meta { font-size: 0.78rem; color: var(--as-text-3); }

        /* Secondary 4 mini stats */
        .abo-dash-4 {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
        }
        @media (max-width: 600px) { .abo-dash-4 { grid-template-columns: repeat(2, 1fr); } }
        .abo-dash-mini {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 14px;
          background: var(--as-surface); border: 1px solid var(--as-border);
          border-radius: var(--as-r); box-shadow: var(--as-shadow);
          border-left: 3px solid transparent;
        }
        .abo-dash-mini--success { border-left-color: var(--as-success); }
        .abo-dash-mini--primary { border-left-color: var(--as-primary); }
        .abo-dash-mini--warning { border-left-color: var(--as-warning); }
        .abo-dash-mini--danger  { border-left-color: var(--as-danger);  }
        .abo-dash-mini-icon { flex-shrink: 0; color: var(--as-text-3); }
        .abo-dash-mini-num  { font-size: 1.1rem; font-weight: 700; color: var(--as-text); line-height: 1; }
        .abo-dash-mini-label{ font-size: 0.75rem; color: var(--as-text-2); margin-top: 2px; }
        .abo-dash-mini { flex-direction: column; align-items: flex-start; }

        /* Plan distribution */
        .abo-dash-dist {
          background: var(--as-surface); border: 1px solid var(--as-border);
          border-radius: var(--as-r); box-shadow: var(--as-shadow); padding: 18px 16px;
        }
        .abo-dash-dist-hd {
          display: flex; align-items: center; gap: 6px;
          font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: .05em; color: var(--as-text-2); margin-bottom: 14px;
        }
        .abo-dash-dist-row {
          display: grid; grid-template-columns: 150px 1fr 70px;
          align-items: center; gap: 10px; margin-bottom: 8px;
        }
        .abo-dash-dist-name {
          font-size: 0.8rem; font-weight: 500; color: var(--as-text);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .abo-dash-dist-track {
          height: 6px; background: var(--as-surface-3);
          border-radius: 99px; overflow: hidden;
        }
        .abo-dash-dist-fill {
          height: 100%; background: var(--as-primary);
          border-radius: 99px; transition: width .4s ease;
        }
        .abo-dash-dist-pct {
          font-size: 0.78rem; font-weight: 600; color: var(--as-text); text-align: right;
        }
        .abo-dash-dist-pct small { font-weight: 400; color: var(--as-text-3); }
      `}</style>
    </div>
  );
}
